import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Toggle } from '../ui/Toggle';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { KeyIcon, RefreshCwIcon, TrashIcon, PlusIcon, UserIcon, ClockIcon } from 'lucide-react';
interface AuthToken {
  id: string;
  name: string;
  lastUsed: string;
  createdAt: string;
  prefix: string;
}
interface TeamMember {
  id: string;
  email: string;
  role: 'admin' | 'viewer';
  addedAt: string;
}
const mockTokens: AuthToken[] = [{
  id: 'tok_1',
  name: 'Production CI/CD',
  lastUsed: '2 hours ago',
  createdAt: '2024-01-01',
  prefix: 'mcp_prod_'
}, {
  id: 'tok_2',
  name: 'Development',
  lastUsed: '1 day ago',
  createdAt: '2024-01-10',
  prefix: 'mcp_dev_'
}, {
  id: 'tok_3',
  name: 'Monitoring Service',
  lastUsed: 'Never',
  createdAt: '2024-01-15',
  prefix: 'mcp_mon_'
}];
const mockTeamMembers: TeamMember[] = [{
  id: 'usr_1',
  email: 'admin@example.com',
  role: 'admin',
  addedAt: '2024-01-01'
}, {
  id: 'usr_2',
  email: 'security@example.com',
  role: 'admin',
  addedAt: '2024-01-05'
}, {
  id: 'usr_3',
  email: 'developer@example.com',
  role: 'viewer',
  addedAt: '2024-01-10'
}];
const tabs = [{
  id: 'tokens',
  label: 'Auth Tokens'
}, {
  id: 'oauth',
  label: 'OAuth / OIDC'
}, {
  id: 'audit',
  label: 'Audit Retention'
}, {
  id: 'exports',
  label: 'Export Destinations'
}, {
  id: 'team',
  label: 'Team Members'
}];
export function Settings() {
  const [activeTab, setActiveTab] = useState('tokens');
  const [retentionDays, setRetentionDays] = useState('90');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  return <div data-id="element-533">
      {/* Header */}
      <div className="mb-6" data-id="element-534">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100" data-id="element-535">
          Settings
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5" data-id="element-536">
          Security and integration configuration
        </p>
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-537" />

      {/* Tab Content */}
      <div className="mt-6" data-id="element-538">
        {activeTab === 'tokens' && <div className="space-y-6" data-id="element-539">
            <Card data-id="element-540">
              <CardHeader title="API Tokens" description="Manage authentication tokens for API access" action={<Button variant="primary" size="sm" data-id="element-542">
                    <PlusIcon className="h-4 w-4 mr-1.5" data-id="element-543" />
                    Create Token
                  </Button>} data-id="element-541" />
              <div className="space-y-3" data-id="element-544">
                {mockTokens.map(token => <div key={token.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-md" data-id="element-545">
                    <div className="flex items-center gap-3" data-id="element-546">
                      <KeyIcon className="h-4 w-4 text-slate-400 dark:text-slate-500" data-id="element-547" />
                      <div data-id="element-548">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-549">
                          {token.name}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-550">
                          <span className="font-mono" data-id="element-551">
                            {token.prefix}••••••••
                          </span>
                          <span className="mx-2" data-id="element-552">•</span>
                          Last used: {token.lastUsed}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-553">
                      <Button variant="ghost" size="sm" data-id="element-554">
                        <RefreshCwIcon className="h-4 w-4" data-id="element-555" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20" data-id="element-556">
                        <TrashIcon className="h-4 w-4" data-id="element-557" />
                      </Button>
                    </div>
                  </div>)}
              </div>
            </Card>
          </div>}

        {activeTab === 'oauth' && <Card data-id="element-558">
            <CardHeader title="OAuth / OIDC Configuration" description="Configure single sign-on for your organization" data-id="element-559" />
            <div className="space-y-4" data-id="element-560">
              <Select label="Provider" options={[{
            value: '',
            label: 'Select provider...'
          }, {
            value: 'okta',
            label: 'Okta'
          }, {
            value: 'auth0',
            label: 'Auth0'
          }, {
            value: 'azure',
            label: 'Azure AD'
          }, {
            value: 'google',
            label: 'Google Workspace'
          }, {
            value: 'custom',
            label: 'Custom OIDC'
          }]} data-id="element-561" />
              <Input label="Issuer URL" placeholder="https://your-domain.okta.com" monospace data-id="element-562" />
              <Input label="Client ID" placeholder="0oa..." monospace data-id="element-563" />
              <Input label="Client Secret" type="password" placeholder="••••••••••••••••" data-id="element-564" />
              <div className="pt-2" data-id="element-565">
                <Button variant="primary" data-id="element-566">Save Configuration</Button>
              </div>
            </div>
          </Card>}

        {activeTab === 'audit' && <Card data-id="element-567">
            <CardHeader title="Audit Log Retention" description="Configure how long audit logs are retained" data-id="element-568" />
            <div className="space-y-4" data-id="element-569">
              <Select label="Retention Period" options={[{
            value: '30',
            label: '30 days'
          }, {
            value: '60',
            label: '60 days'
          }, {
            value: '90',
            label: '90 days'
          }, {
            value: '180',
            label: '180 days'
          }, {
            value: '365',
            label: '1 year'
          }, {
            value: 'unlimited',
            label: 'Unlimited'
          }]} value={retentionDays} onChange={e => setRetentionDays(e.target.value)} data-id="element-570" />
              <div className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-md" data-id="element-571">
                <ClockIcon className="h-4 w-4 text-slate-400 dark:text-slate-500 mt-0.5" data-id="element-572" />
                <div className="text-xs text-slate-600 dark:text-slate-300" data-id="element-573">
                  <p data-id="element-574">
                    Audit logs older than the retention period will be
                    automatically deleted.
                  </p>
                  <p className="mt-1" data-id="element-575">
                    Current storage usage: <strong data-id="element-576">2.4 GB</strong>
                  </p>
                </div>
              </div>
              <div className="pt-2" data-id="element-577">
                <Button variant="primary" data-id="element-578">Save Settings</Button>
              </div>
            </div>
          </Card>}

        {activeTab === 'exports' && <div className="space-y-6" data-id="element-579">
            <Card data-id="element-580">
              <CardHeader title="Webhook Destination" description="Send audit events to an external endpoint" data-id="element-581" />
              <div className="space-y-4" data-id="element-582">
                <Toggle label="Enable Webhook" description="Send real-time audit events to your webhook endpoint" checked={webhookEnabled} onChange={setWebhookEnabled} data-id="element-583" />
                {webhookEnabled && <>
                    <Input label="Webhook URL" placeholder="https://your-siem.example.com/webhook" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} monospace data-id="element-584" />
                    <Select label="Events to Send" options={[{
                value: 'all',
                label: 'All events'
              }, {
                value: 'denied',
                label: 'Denied events only'
              }, {
                value: 'egress',
                label: 'Blocked egress only'
              }]} data-id="element-585" />
                    <div className="pt-2" data-id="element-586">
                      <Button variant="secondary" size="sm" data-id="element-587">
                        Test Webhook
                      </Button>
                    </div>
                  </>}
              </div>
            </Card>

            <Card data-id="element-588">
              <CardHeader title="SIEM Integration" description="Connect to your security information and event management system" data-id="element-589" />
              <div className="space-y-4" data-id="element-590">
                <Select label="SIEM Provider" options={[{
              value: '',
              label: 'Select provider...'
            }, {
              value: 'splunk',
              label: 'Splunk'
            }, {
              value: 'datadog',
              label: 'Datadog'
            }, {
              value: 'elastic',
              label: 'Elastic Security'
            }, {
              value: 'sentinel',
              label: 'Microsoft Sentinel'
            }]} data-id="element-591" />
                <p className="text-xs text-slate-500 dark:text-slate-400" data-id="element-592">
                  Configuration options will appear based on your selected
                  provider.
                </p>
              </div>
            </Card>
          </div>}

        {activeTab === 'team' && <Card data-id="element-593">
            <CardHeader title="Team Members" description="Manage who has access to MCPGuard Cloud" action={<Button variant="primary" size="sm" data-id="element-595">
                  <PlusIcon className="h-4 w-4 mr-1.5" data-id="element-596" />
                  Invite Member
                </Button>} data-id="element-594" />
            <div className="space-y-3" data-id="element-597">
              {mockTeamMembers.map(member => <div key={member.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-md" data-id="element-598">
                  <div className="flex items-center gap-3" data-id="element-599">
                    <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center" data-id="element-600">
                      <UserIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" data-id="element-601" />
                    </div>
                    <div data-id="element-602">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-603">
                        {member.email}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-604">
                        Added {member.addedAt}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-605">
                    <Badge variant={member.role === 'admin' ? 'warning' : 'neutral'} data-id="element-606">
                      {member.role}
                    </Badge>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20" data-id="element-607">
                      <TrashIcon className="h-4 w-4" data-id="element-608" />
                    </Button>
                  </div>
                </div>)}
            </div>
          </Card>}
      </div>
    </div>;
}